/**
 * Pass Service
 * 
 * API service layer for pass-specific operations.
 * 
 * Handles:
 * - Creating user passes (purchase)
 *
 * Note: For fetching available passes in the purchase flow,
 * use `subscriptionService.getAvailableSubscriptions()` which returns
 * both memberships and passes in a single call.
 *
 * @module subscription/api
 */

import { apiClient } from '~/lib/apiClient';
import { appRuntime } from '~/services/appRuntime';
import type { ApiResult } from '~/lib/apiClient/types';
import type { ApiPass } from '@lgc/database/types/domains/pass';
import type { CreateUserPassRequest, CreateUserPassResponse } from '../types';

/**
 * Pass Service
 *
 * Handles all pass-specific API operations
 */
export const passService = {

  /**
   * Fetch ONE pass by id.
   *
   * Exists for the `?plan=` deep link. `getAvailableSubscriptions()` returns only
   * `public` plans, so an UNLISTED plan can never be found there — this endpoint
   * serves `public` and `unlisted` alike and 404s the rest, which is what makes a
   * shared link work at all.
   *
   * (These by-id helpers were deleted as dead code in the public-API cleanup;
   * they are back because unlisted plans gave them a real consumer.)
   *
   * A restricted or missing plan is a 404 either way — deliberately
   * indistinguishable, so the id space says nothing about what exists.
   */
  async getPassDetails(passId: number): Promise<ApiResult<{ pass: ApiPass }>> {
    const tenantHandle = appRuntime.tenantHandle;

    return await apiClient.get<{ pass: ApiPass }>(
      `/passes/details/${passId}`,
      { tenantHandle: tenantHandle || undefined },
    );
  },
  /**
   * Create a user pass (purchase)
   * 
   * Creates a new user pass after payment processing.
   * 
   * @param request - Pass purchase request payload
   * @returns ApiResult with created user pass
   * 
   * @example
   * ```typescript
   * const result = await passService.createUserPass({
   *   userId: 123,
   *   passId: 789,
   *   clubId: 2,
   *   paymentMethodId: 'pm_123456',
   * });
   * 
   * if (result.success) {
   *   console.log('User pass created:', result.payload.userPassId);
   * }
   * ```
   */
  async createUserPass(
    request: CreateUserPassRequest
  ): Promise<ApiResult<CreateUserPassResponse>> {
    const tenantHandle = appRuntime.tenantHandle;
    
    return await apiClient.post<CreateUserPassResponse>(
      '/userPasses',
      request,
      {
        tenantHandle: tenantHandle || undefined,
        clubId: request.clubId,
      }
    );
  },

  /**
   * Advisory pre-check: can this address receive a gifted pass?
   *
   * ONE bit back, on purpose — the server never says why. Authenticated only;
   * the purchase itself re-runs the same rules before any charge, so a caller
   * that cannot ask (a guest) loses the early warning and nothing else.
   */
  async checkGiftEligibility(
    request: { email: string; clubId: number }
  ): Promise<ApiResult<{ eligible: boolean }>> {
    const tenantHandle = appRuntime.tenantHandle;
    return await apiClient.post<{ eligible: boolean }>(
      '/userPasses/gift-eligibility',
      { email: request.email },
      { tenantHandle: tenantHandle || undefined, clubId: request.clubId },
    );
  },
};
